<div class="wrapper flex flex-col justify-between" data-testid="risk-detail-panel">
  <div>
    <div class="heading-row flex justify-between items-start mb-[40px]">
      <div class="heading text-type-2 truncate">{{ title }}</div>
      <div class="flex flex-row items-center gap-[4px]">
        @if (hasMenu) {
          <div class="more-menu-container">
            <button mat-icon-button [matMenuTriggerFor]="moreMenu" class="more-menu-button" aria-label="More actions" data-testid="risk-detail-more"><mat-icon>more_horiz</mat-icon></button>
          </div>
          <mat-menu #moreMenu="matMenu" class="risk-menu">
            @if (canEdit) {
              <button mat-menu-item (click)="edit()" data-testid="risk-edit"><mat-icon svgIcon="pencil-outline"></mat-icon><span>Edit risk</span></button>
            }
            @if (canManage) {
              <button mat-menu-item (click)="toggleInternal()" data-testid="risk-toggle-internal"><mat-icon [svgIcon]="risk.internal ? 'lock-open' : 'lock'"></mat-icon><span>{{ risk.internal ? 'Make visible to contractor' : 'Mark as internal' }}</span></button>
            }
            @if (canConvert && risk.status === 'open') {
              <button mat-menu-item (click)="convert()" data-testid="risk-convert"><mat-icon svgIcon="arrow-right"></mat-icon><span>Convert to issue</span></button>
            }
            @if (canClose && risk.status === 'open') {
              <button mat-menu-item (click)="close()" data-testid="risk-close"><mat-icon svgIcon="check-circle"></mat-icon><span>Close risk</span></button>
            }
            @if (canReopen && risk.status === 'closed') {
              <button mat-menu-item (click)="reopen()" data-testid="risk-reopen"><mat-icon svgIcon="undo"></mat-icon><span>Reopen</span></button>
            }
            @if (canDelete) {
              <button mat-menu-item (click)="remove()" class="text-red-500" data-testid="risk-delete"><mat-icon svgIcon="trash" class="text-red-500"></mat-icon><span>Delete</span></button>
            }
          </mat-menu>
        }
      </div>
    </div>

    @if (loading && !risk) {
      <div class="text-type-14 secondary-text">Loading…</div>
    }
    @if (risk) {
      @if (risk.status === 'pending' && canApprove) {
        <div class="pending-banner mb-[16px]" data-testid="pending-banner">
          <div>Proposed by {{ risk.proposedBy ? risk.proposedBy.first_name + ' ' + risk.proposedBy.last_name : 'a team member' }} on {{ risk.created_at | date: 'MMM d, y' }}.</div>
        </div>
      }
      <mat-tab-group [(selectedIndex)]="tabIndex" class="panel-tabs" animationDuration="0ms">
        <mat-tab label="Details">
          <div class="details pt-[40px]">
            <div class="field">
              <div class="label">Status</div>
              <div class="value flex flex-row flex-wrap items-center gap-[8px]">
                <div class="status-chip px-[14px] rounded-full" [ngClass]="[statusMap[risk.status].bgColor, statusMap[risk.status].textColor]" data-testid="risk-status">{{ statusMap[risk.status].label }}</div>
                @if (risk.status === 'converted' && risk.issue) {
                  <a class="link" [routerLink]="['/projects', data.projectId]" [queryParams]="{ tab: 'issues', selectedId: risk.issue.id }" data-testid="risk-issue-link">→ Issue: {{ risk.issue.title }}</a>
                }
              </div>
            </div>
            <div class="two">
              <div class="field"><div class="label">Score</div><div class="value"><app-risk-score-chip [score]="risk.score" /></div></div>
              <div class="field"><div class="label">Identified</div><div class="value">{{ risk.identified_at ? (risk.identified_at | date: 'MMM d, y') : '—' }}</div></div>
            </div>

            <div class="field">
              <div class="label">Risk description</div>
              <div class="value">{{ risk.description }}</div>
            </div>
            @if (risk.project_impact) {
              <div class="field">
                <div class="label">Project impact</div>
                <div class="value">{{ risk.project_impact }}</div>
              </div>
            }
            <div class="two">
              <div class="field"><div class="label">Probability</div><div class="value">{{ risk.probability }} of 5</div></div>
              <div class="field"><div class="label">Impact</div><div class="value">{{ risk.impact }} of 5</div></div>
              <div class="field">
                <div class="label">Risk areas</div>
                <div class="value">
                  <div class="tags">
                    @for (a of risk.risk_areas; track a) {
                      <span class="risk-area-tag">{{ a }}</span>
                    } @empty {
                      —
                    }
                  </div>
                </div>
              </div>
              <div class="field"><div class="label">Discipline</div><div class="value">{{ risk.discipline || '—' }}</div></div>
              <div class="field">
                <div class="label">Owner</div>
                <div class="value" data-testid="risk-owner">
                  @if (risk.owner) {
                    <div class="owner"><span class="avatar">{{ ownerInitials() }}</span><span>{{ owner() }}</span></div>
                    @if (risk.owner.email) {
                      <div class="text-type-14 secondary-text">{{ risk.owner.email }}</div>
                    }
                  } @else {
                    <span class="secondary-text">Unassigned</span>
                  }
                </div>
              </div>
              <div class="field"><div class="label">Response type</div><div class="value">{{ risk.response_type ? responseLabel[risk.response_type] : '—' }}</div></div>
            </div>
            @if (risk.response_strategy) {
              <div class="field">
                <div class="label">Response strategy</div>
                <div class="value">{{ risk.response_strategy }}</div>
              </div>
            }
            @if (risk.response_action) {
              <div class="field">
                <div class="label">Action to implement</div>
                <div class="value">{{ risk.response_action }}</div>
              </div>
            }
            @if (risk.attachments?.length) {
              <div class="field" data-testid="risk-attachments">
                <div class="label">Attachments</div>
                <div class="pictures-row flex flex-wrap gap-[20px]">
                  @for (a of risk.attachments; track a.id) {
                    <div class="picture-block">
                      @if (a.thumb_url) {
                        <app-image (click)="appService.openCarousel($event, risk.attachments, a, false)" [url]="a.thumb_url" class="each-picture" />
                      } @else {
                        <div (click)="appService.openCarousel($event, risk.attachments, a, false)" (keyup.enter)="appService.openCarousel($event, risk.attachments, a, false)" tabindex="0" role="button" class="each-document"><span class="file-title">{{ a.original_name || 'Attachment' }}</span></div>
                      }
                    </div>
                  }
                </div>
              </div>
            }
            @if (canManage) {
              <div class="field">
                <div class="label">Internal</div>
                <div class="value">{{ risk.internal ? 'Yes, hidden from contractors' : 'No, visible to contractors' }}</div>
              </div>
            }
          </div>
        </mat-tab>
        <mat-tab [label]="'Updates (' + updatesTotal + ')'">
          <!-- Either the form or the timeline: history stays out of the way while composing. -->
          @if (showUpdateForm && canUpdate) {
            <app-risk-update-form [risk]="risk" (saved)="onUpdateSaved()" (cancelled)="showUpdateForm = false" class="block pt-[40px]" />
          } @else {
            <app-risk-updates-timeline [updates]="updates" [total]="updatesTotal" [projectTimezone]="projectTimezone" (loadMore)="loadUpdates()" />
          }
        </mat-tab>
      </mat-tab-group>
    }
  </div>

  @if (risk && !composingUpdate && (showApproveActions || canUpdate)) {
    <div class="action-section w-full flex justify-between items-center gap-[10px] mt-[16px]">
      @if (showApproveActions) {
        <app-secondary-button (handleClick)="reject()" class="flex-1" data-testid="risk-reject">Reject…</app-secondary-button>
        <app-primary-button (handleClick)="approve()" class="flex-1" data-testid="risk-approve">Approve</app-primary-button>
      } @else if (canUpdate) {
        <app-primary-button (handleClick)="openUpdateForm()" class="flex-1" data-testid="risk-add-update">Add update</app-primary-button>
      }
    </div>
  }
</div>
